<template lang="html">
    <map-popup
        class="map-popup licence"
        :title="title"
        @close-popup="$emit('close-popup')"
    >
        <section class="licence-details">
            <div
                v-for="licenceProp in popupContents"
                :key="licenceProp.title"
                class="licence-property"
            >
                <h3
                    class="title is-5 has-text-white is-marginless"
                    v-text="licenceProp.title"
                />
                <span
                    class="is-size-6"
                    v-text="licenceProp.value"
                />
            </div>
        </section>
    </map-popup>
</template>

<script>
import MapPopup from '@foundryspatial/map-popup';
import moment from 'moment';

export default {
    name: 'LicencePopup',

    components: {
        MapPopup,
    },

    props: {
        licenceData: {
            type: Object,
            required: true,
        },
    },

    computed: {
        title() {
            return this.replaceBlank(this.licenceData.licensee, 'N/A');
        },

        // format licence data into "title" and "value" pairs to loop over
        popupContents() {
            const licence = this.licenceData;
            window.gtag('event', 'Licence', { action: 'licence-click', label: licence.licence_no });
            if (licence.term === 'short') {
                return [
                    {
                        title: 'Issuing Organization',
                        value: this.replaceBlank(licence.net, '-'),
                    },
                    {
                        title: 'App. File',
                        value: this.replaceBlank(licence.licence_no, '-'),
                    },
                    {
                        title: 'POD',
                        value: this.replaceBlank(licence.pod, '-'),
                    },
                    {
                        title: 'Start Date',
                        value: this.replaceBlank(licence.start_date, 'No Date', this.formatDate),
                    },
                    {
                        title: 'Expiry Date',
                        value: this.replaceBlank(licence.expiry_date, 'No Date', this.formatDate),
                    },
                    {
                        title: 'Purpose',
                        value: this.replaceBlank(licence.purpose, '-'),
                    },
                    {
                        title: 'Source',
                        value: this.replaceBlank(licence.stream_name, '-'),
                    },
                    {
                        title: 'Source Type, Term',
                        value: this.replaceBlank(licence.type_term_display, '-'),
                    },
                    {
                        title: 'Volume',
                        value: this.replaceBlank(licence.qty_display, '-'),
                    },
                ];
            }
            // long-term licences
            return [
                {
                    title: 'Licence',
                    value: this.replaceBlank(licence.licence_no, '-'),
                },
                {
                    title: 'POD',
                    value: this.replaceBlank(licence.pod, '-'),
                },
                {
                    title: 'Priority Date',
                    value: this.replaceBlank(licence.priority_date, 'No Date', this.formatDate),
                },
                {
                    title: 'Status Date',
                    value: this.replaceBlank(licence.lic_status_date, 'No Date', this.formatDate),
                },
                {
                    title: 'Quantity',
                    value: this.replaceBlank(licence.qty_display, '-'),
                },
                {
                    title: 'Source',
                    value: this.replaceBlank(licence.stream_name, '-'),
                },
                {
                    title: 'Source Type, Term',
                    value: this.replaceBlank(licence.type_term_display, '-'),
                },
                {
                    title: 'Purpose',
                    value: this.replaceBlank(licence.purpose, '-'),
                },
                {
                    title: 'Status',
                    value: this.replaceBlank(licence.status, '-'),
                },
                {
                    title: 'Flag',
                    value: this.replaceBlank(licence.flag, '-'),
                },
            ];
        },
    },

    methods: {
        /**
         * Replace blank/null values with a placeholder for display, or optionally apply a formatting function and
         * return the original string
         *
         * @param {string} val - input value
         * @param {string} replacement - substitution to use for blank inputs
         * @param {Function} format - optional formatting method
         * @returns {string} - string for display
         */
        replaceBlank(val, replacement, format = null) {
            if (val === null || val === '') {
                return replacement;
            }

            // if the value isn't blank,
            // optionally pass it to a formatting function
            if (format) {
                return format(val);
            }

            return val;
        },

        /**
         * Apply date formatting to timestamps from the db
         *
         * @param {string} date - timestamp value
         * @returns {string} - formatted date string
         */
        formatDate(date) {
            return moment(date).format('MMM DD, YYYY');
        },
    },
};
</script>

<style lang="scss">
.map-popup.licence {
    .licence-details {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-gap: 0.5rem;
    }
}
</style>
